'use client'

import { useEffect, useMemo } from 'react'
import { usePathname, useRouter } from 'next/navigation'

import { SearchModal } from '@common'
import { AcademyMobileMenu as AuthMobileMenu } from '@common/components/MobileMenu/variants/AcademyMobileMenu'
import { IS_CODE_SANDBOX_ENABLED } from '@common/components/MobileMenu/variants/AcademyMobileMenu/academy-mobile-menu.const'
import { Sidebar } from '@common/components/Sidebar'
import { useAuthUser } from '@common/hooks/useAuthUser'
import { navigateToMenuUrl } from '@common/utils/navigateToMenuUrl'

import { useLayoutStore } from '@stores/layout/layout.store'

import { ACADEMY_ROUTES } from '@/shared/academy.const'
import { ACADEMY_LAYOUT_TEST_IDS } from '@/shared/academy-test-ids.const'
import { AcademyMobileMenu as GuestMobileMenu } from '@/shared/components/AcademyMobileMenu'
import { useAcademySearch } from '@/shared/components/AcademySearchModal'

import { AcademyHeaderWrapper } from '../AcademyHeaderWrapper'
import { CodeSandboxMenu } from '../CodeSandboxMenu/CodeSandboxMenu'
import { DocumentationMenu } from '../DocumentationMenu'
import { ScrollToTopButton } from '../ScrollToTopButton'
import { TutorialMenu } from '../TutorialMenu'

import { SIDEBAR_MENU_KEYS } from './academy-layout.const'
import { useIsMobile, useMobileMenu, useNavigationIntercept } from './hooks'
import type { AcademyLayoutProps } from './types'
import { buildMenuItems, getLayoutClassName } from './utils'

export function AcademyLayout({
  children,
  sidebarChildren,
  tutorialSections,
  currentTutorialPath,
  breadcrumbs,
  categoryTree,
  rootArticles,
}: AcademyLayoutProps) {
  const { sidebarCollapsed } = useLayoutStore()
  const { user } = useAuthUser()
  const isLoggedIn = !!user
  const pathname = usePathname()
  const router = useRouter()
  const { openModal: openDocumentationSearch, searchModalProps: documentationSearchModalProps } = useAcademySearch()

  useNavigationIntercept()

  const isMobile = useIsMobile()
  const { isMobileMenuOpen, handleToggle, handleClose } = useMobileMenu(pathname, isMobile)

  const isQuickStartPage = pathname.startsWith(ACADEMY_ROUTES.quickStart)
  const isDocumentationPage = pathname.startsWith(ACADEMY_ROUTES.documentation)
  const isCodeSandboxPage = pathname.startsWith(ACADEMY_ROUTES.codeSandbox) && IS_CODE_SANDBOX_ENABLED

  const menuItems = useMemo(() => buildMenuItems(pathname), [pathname])
  const layoutClassName = getLayoutClassName(sidebarCollapsed, false)

  useEffect(() => {
    const handleGlobalSearchShortcut = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') {
        return
      }
      if (event.defaultPrevented) {
        return
      }
      if (documentationSearchModalProps.open) {
        return
      }
      const target = event.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return
      }
      event.preventDefault()
      openDocumentationSearch()
    }

    window.addEventListener('keydown', handleGlobalSearchShortcut)
    return () => window.removeEventListener('keydown', handleGlobalSearchShortcut)
  }, [documentationSearchModalProps.open, openDocumentationSearch])

  const finalSidebarChildren = useMemo(() => {
    if (isQuickStartPage && tutorialSections?.length) {
      return (
        <>
          {sidebarChildren}
          <TutorialMenu
            sections={tutorialSections}
            currentPath={currentTutorialPath}
            basePath={ACADEMY_ROUTES.quickStart}
            showCompletion={false}
          />
        </>
      )
    }

    if (isDocumentationPage && tutorialSections?.length) {
      return (
        <>
          {sidebarChildren}
          <DocumentationMenu
            sections={tutorialSections}
            currentPath={currentTutorialPath}
            categoryTree={categoryTree}
            rootArticles={rootArticles}
          />
        </>
      )
    }

    if (isCodeSandboxPage) {
      return (
        <>
          {sidebarChildren}
          <CodeSandboxMenu />
        </>
      )
    }

    return sidebarChildren
  }, [
    isQuickStartPage,
    isDocumentationPage,
    isCodeSandboxPage,
    tutorialSections,
    currentTutorialPath,
    sidebarChildren,
    categoryTree,
    rootArticles,
  ])

  const sidebar = useMemo(() => {
    const menuKey = isCodeSandboxPage
      ? SIDEBAR_MENU_KEYS.codeSandbox
      : isQuickStartPage && tutorialSections?.length
        ? SIDEBAR_MENU_KEYS.tutorial
        : SIDEBAR_MENU_KEYS.normal

    return (
      <Sidebar
        key={menuKey}
        selectedNavId="academy"
        menuGroups={menuItems}
        navbarTopSlot={null}
        showInviteButton={false}
        showWorkspaceSelector={false}
      >
        {finalSidebarChildren}
      </Sidebar>
    )
  }, [menuItems, finalSidebarChildren, isQuickStartPage, isCodeSandboxPage, tutorialSections])

  return (
    <div className={layoutClassName} data-testid={ACADEMY_LAYOUT_TEST_IDS.root}>
      <aside className="sidebar">{sidebar}</aside>
      <div className="main-content">
        <header className="topbar">
          <AcademyHeaderWrapper
            breadcrumbs={breadcrumbs}
            onMobileMenuToggle={handleToggle}
            isMobile={isMobile}
            documentationSearchEnabled
            onDocumentationSearchOpen={openDocumentationSearch}
          />
        </header>
        <main className={`content${isCodeSandboxPage ? ' code-sandbox-content' : ''}`}>{children}</main>
      </div>
      {isLoggedIn ? (
        <AuthMobileMenu
          isOpen={isMobileMenuOpen}
          onClose={handleClose}
          tutorialSections={tutorialSections}
          currentTutorialPath={currentTutorialPath}
          onNavigationClick={(url) => navigateToMenuUrl(url, router)}
          categoryTree={categoryTree}
          rootArticles={rootArticles}
        />
      ) : (
        <GuestMobileMenu
          isOpen={isMobileMenuOpen}
          onClose={handleClose}
          tutorialSections={tutorialSections}
          currentTutorialPath={currentTutorialPath}
          categoryTree={categoryTree}
          rootArticles={rootArticles}
        />
      )}
      <ScrollToTopButton />
      <SearchModal {...documentationSearchModalProps} />
    </div>
  )
}
